import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Breadcrumb, Button, Dropdown } from 'react-bootstrap';
import { API_ENDPOINTS } from '../config/api';
import NotificationsDropdown from './NotificationsDropdown';
import { Sun, Moon, Megaphone, ChevronDown } from 'lucide-react';

/**
 * Reusable top bar for Super Admin and Institutional Admin dashboards.
 * Props:
 *   variant: 'super' | 'institutional'
 *   breadcrumbItems: Array<{ href?: string, label: string }> - last item is active (no href)
 */
const AdminTopBar = ({ variant = 'super', breadcrumbItems = [] }) => {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('theme');
    return stored ? stored === 'dark' : true;
  });
  const [userData, setUserData] = useState({
    firstName: localStorage.getItem('firstName'),
    lastName: localStorage.getItem('lastName'),
    profilePic: localStorage.getItem('profilePic'),
    email: localStorage.getItem('email')
  });
  const [profilePicDataUrl, setProfilePicDataUrl] = useState(null);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  // Institutional announcements (institutional admins only) – read-only list
  // of currently active announcements. Create / edit / delete now live on
  // the dedicated Announcements page.
  const [instAnnouncements, setInstAnnouncements] = useState([]);
  const [instAnnouncementsLoading, setInstAnnouncementsLoading] = useState(false);
  const [instAnnouncementsError, setInstAnnouncementsError] = useState('');

  const isSuper = variant === 'super';
  const prefix = isSuper ? 'super' : 'inst-admin';
  const profileButtonId = `admin-topbar-${prefix}-profile-button`;
  const profileDropdownId = `admin-topbar-${prefix}-profile-dropdown`;
  const defaultDisplayName = isSuper ? 'Super Admin' : 'Institutional Admin';

  useEffect(() => {
    document.body.setAttribute('data-bs-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const loadProfilePicture = async (profilePicPath) => {
    if (!profilePicPath) return null;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_ENDPOINTS.PROFILE_PICS}${profilePicPath}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return null;
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${API_ENDPOINTS.AUTH}/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUserData({
            firstName: data.firstName,
            lastName: data.lastName,
            profilePic: data.profilePic,
            email: data.email
          });
          if (data.profilePic) {
            const picUrl = await loadProfilePicture(data.profilePic);
            setProfilePicDataUrl(picUrl);
          }
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchUserData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const dd = document.getElementById(profileDropdownId);
      const btn = document.getElementById(profileButtonId);
      if (profileDropdownOpen && dd && btn && !dd.contains(e.target) && !btn.contains(e.target)) {
        setProfileDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileDropdownOpen, profileDropdownId, profileButtonId]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('firstName');
    localStorage.removeItem('lastName');
    localStorage.removeItem('profilePic');
    localStorage.removeItem('role');
    localStorage.removeItem('notifications');
    localStorage.removeItem('lastNotificationTime');
    window.dispatchEvent(new CustomEvent('app-logout'));
    navigate('/');
  };

  const getInitials = () => {
    const f = userData.firstName?.charAt(0) || (isSuper ? 'S' : 'I');
    const l = userData.lastName?.charAt(0) || 'A';
    return `${f}${l}`.toUpperCase();
  };

  const displayName = userData.firstName && userData.lastName
    ? `${userData.firstName} ${userData.lastName}`
    : defaultDisplayName;

  const topBarClassName = 'bg-inverse border-start sticky-top border-bottom d-flex align-items-center justify-content-between px-4';

  const toggleClassName = 'no-arrow d-flex align-items-center gap-2 border-0 bg-transparent p-1';

  const loadActiveInstitutionAnnouncements = async () => {
    if (isSuper) return;
    try {
      setInstAnnouncementsError('');
      setInstAnnouncementsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('You must be signed in to view announcements.');
      }
      const res = await fetch(`${API_ENDPOINTS.INSTITUTIONAL_ADMIN}/announcements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to load announcements');
      }
      const data = await res.json();
      const todayStr = new Date().toISOString().slice(0, 10);
      // Filter to currently active announcements (today within start/end)
      const active = (data || []).filter((ann) => {
        const start = ann.startDate ? String(ann.startDate).slice(0, 10) : null;
        const end = ann.endDate ? String(ann.endDate).slice(0, 10) : null;
        if (start && todayStr < start) return false;
        if (end && todayStr > end) return false;
        return true;
      });
      // De-duplicate (server creates one row per program under the institution).
      const seen = new Map();
      for (const ann of active) {
        const key = `${ann.title}|${ann.startDate}|${ann.endDate}|${ann.content}|${ann.type}`;
        if (!seen.has(key)) seen.set(key, ann);
      }
      setInstAnnouncements(Array.from(seen.values()));
    } catch (err) {
      setInstAnnouncementsError(err.message || 'Failed to load announcements');
    } finally {
      setInstAnnouncementsLoading(false);
    }
  };

  return (
    <div className={topBarClassName} style={{ minHeight: 61 }}>
      <Breadcrumb className="mb-0 py-2">
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          return (
            <Breadcrumb.Item
              key={index}
              href={isLast ? undefined : item.href}
              active={isLast}
            >
              {item.label}
            </Breadcrumb.Item>
          );
        })}
      </Breadcrumb>
      <div className="d-flex align-items-center">
        <Button
          variant="light-lite"
          size="sm"
          className="d-flex align-items-center"
          onClick={() => setDarkMode((prev) => !prev)}
          title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {darkMode ? <Sun /> : <Moon />}
        </Button>
        {/* Institutional active-announcements dropdown (read-only) */}
        {!isSuper && (
          <Dropdown
            align="end"
            onToggle={(nextShow) => {
              if (nextShow) {
                loadActiveInstitutionAnnouncements();
              }
            }}
          >
            <Dropdown.Toggle
              variant="light-lite"
              size="sm"
              className="d-flex align-items-center no-arrow"
              title="Active announcements"
            >
              <Megaphone />
            </Dropdown.Toggle>
            <Dropdown.Menu align="end" style={{ minWidth: 280 }}>
              <Dropdown.Header className="d-flex justify-content-between align-items-center">
                <span>Active announcements</span>
                <Button
                  variant="light-solid"
                  size="sm"
                  className="p-1 px-2 text-decoration-none"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate('/institutional-admin/announcements');
                  }}
                >
                  Manage
                </Button>
              </Dropdown.Header>
              {instAnnouncementsLoading && (
                <Dropdown.Item disabled className="small text-muted">
                  Loading...
                </Dropdown.Item>
              )}
              {instAnnouncementsError && (
                <Dropdown.Item disabled className="small text-danger">
                  {instAnnouncementsError}
                </Dropdown.Item>
              )}
              {!instAnnouncementsLoading && !instAnnouncementsError && instAnnouncements.length === 0 && (
                <Dropdown.Item disabled className="small text-muted">
                  No active announcements
                </Dropdown.Item>
              )}
              {!instAnnouncementsLoading && instAnnouncements.map((ann) => (
                <Dropdown.Item
                  key={ann.id}
                  className="small"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/institutional-admin/announcements');
                  }}
                >
                  <div className="d-flex flex-column">
                    <span className="fw-semibold text-truncate" style={{ maxWidth: 240 }}>
                      {ann.title}
                    </span>
                    <span className="text-muted text-truncate" style={{ maxWidth: 240 }}>
                      {ann.content}
                    </span>
                    <span className="text-muted small">
                      {ann.startDate} – {ann.endDate}
                    </span>
                  </div>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        )}
        <NotificationsDropdown />
        <Dropdown align="end" show={profileDropdownOpen} onToggle={setProfileDropdownOpen}>
          <Dropdown.Toggle
            id={profileButtonId}
            variant="light-lite"
            className={toggleClassName}
          >
            {profilePicDataUrl ? (
              <img src={profilePicDataUrl} alt="Profile" className="rounded-circle" style={{ width: 28, height: 28, objectFit: 'cover' }} />
            ) : (
              <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center small fw-semibold" style={{ width: 28, height: 28 }}>
                {getInitials()}
              </div>
            )}
            <span className="d-none d-md-inline text-body">{displayName}</span>
             <ChevronDown />
          </Dropdown.Toggle>
          <Dropdown.Menu id={profileDropdownId} align="end">
            <Dropdown.Header>
              <div className="d-flex align-items-center gap-2">
                {profilePicDataUrl ? (
                  <img src={profilePicDataUrl} alt="Profile" className="rounded-circle" style={{ width: 36, height: 36, objectFit: 'cover' }} />
                ) : (
                  <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-semibold" style={{ width: 36, height: 36 }}>
                    {getInitials()}
                  </div>
                )}
                <div className="d-flex flex-column lh-sm">
                  <span className="fw-semibold">{displayName}</span>
                  <span className="small text-muted">{userData.email}</span>
                </div>
              </div>
            </Dropdown.Header>
            <Dropdown.Divider />
            <Dropdown.Item href="/profile?tab=profile">Profile</Dropdown.Item>
            <Dropdown.Item href="/profile?tab=documents">Documents</Dropdown.Item>
            <Dropdown.Item href="/profile?tab=settings">Settings</Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  );
};

export default AdminTopBar;
